<script setup lang="ts">
import axios from 'axios'
import { useCloned } from '@vueuse/core'
import dayjs from 'dayjs'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'

import BaseVueTelInput from '@/components/BaseVueTelInput.vue'
import { Trash2 } from 'lucide-vue-next'
import MissedCallsAudio from './MissedCallsAudio.vue'

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/ui/alert-dialog'

import type { PhoneObject } from '@/components/BaseVueTelInput.vue'
import type { Icons } from '@/components/base/BaseIcon.vue'

interface Method {
  id: number
  type: 'direct' | 'indirect'
  title: string
  description: string
  value: boolean
}

interface Form {
  message: string
  messageLink: string
  shortedMessageLink: string
  vocalMessage: File | null
  audioName: string
  missedCallsMethod: Method | Record<string, never>
  storePhoneNumber: string
  landingPageTitle: string
  landingPageDescription: string
  isLandingPageEnabled: boolean
}

interface CancelModal {
  isDisplayed: boolean
  linkText: string
  cancel: string
  title: string
  text: string
  icon: Icons
  color: string
  to: string
  hasConfirmed: boolean
}

interface Merchant {
  id: number
  [key: string]: any
}

interface Store {
  id: number
  name: string
  [key: string]: any
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const notification = useNotification()
const { merchantName, user, stores, isSupportConnection } = useLocalStorage()
const { areEquals } = useObject()
const { isUrlValid } = useRegex()

const isLoading = ref(true)
const isFixed = ref(false)
const missedCallStoreId = ref<number | null>(null)
const isDeleteModalOpen = ref(false)
const isDeleting = ref(false)
const merchant = ref<Merchant>({})
const store = ref<Store>({})
const storeId = (route?.params?.storeId as string) ?? ''
const rightColumn = ref<HTMLElement>()
const countryCode = ref<CountryCode | null>(null)

const canShowDeleteButton = computed(() => isSupportConnection.value && !!missedCallStoreId.value)
const detectCountryFromNumber = (rawNumber: string) => {
  const phoneNumber = parsePhoneNumberWithError('+' + rawNumber)

  return {
    formattedNumber: phoneNumber.formatInternational(),
    countryCode: phoneNumber.country,
    isValid: phoneNumber.isValid(),
    nationalFormat: phoneNumber.formatNational()
  }
}

const methods = ref<Method[]>([
  {
    id: 1,
    type: 'direct',
    title: t('pages.missedCalls.missedCallsDetailPage.message.form.behavior.methods.direct.title'),
    description: t('pages.missedCalls.missedCallsDetailPage.message.form.behavior.methods.direct.description'),
    value: false
  },
  {
    id: 2,
    type: 'indirect',
    title: t('pages.missedCalls.missedCallsDetailPage.message.form.behavior.methods.indirect.title'),
    description: t('pages.missedCalls.missedCallsDetailPage.message.form.behavior.methods.indirect.description'),
    value: true
  }
])

const form = reactive<Form>({
  message: t('pages.missedCalls.missedCallsDetailPage.message.form.defaultSmsText'),
  messageLink: '',
  shortedMessageLink: 'pngo.fr/missed-call',
  vocalMessage: null,
  audioName: t('pages.missedCalls.missedCallsDetailPage.audioRecorder.defaultName'),
  missedCallsMethod: methods.value[0],
  storePhoneNumber: '',
  landingPageTitle: t('pages.missedCalls.missedCallsDetailPage.message.form.userData.defaultTitleLandingPage'),
  landingPageDescription: t(
    'pages.missedCalls.missedCallsDetailPage.message.form.userData.defaultDescriptionLandingPage'
  ),
  isLandingPageEnabled: false
})

const invalidCharacters = ref(false)
const invalidSmsContent = ref(false)
const validPhoneNumber = ref(true)
const vocalMessageUpdated = ref(false)
const isProcessing = ref(false)

const cancelModal = reactive<CancelModal>({
  isDisplayed: false,
  linkText: t('global.words.submit'),
  cancel: t('global.words.cancel'),
  title: t('pages.missedCalls.missedCallsDetailPage.cancelModal.title'),
  text: t('pages.missedCalls.missedCallsDetailPage.cancelModal.description'),
  icon: 'HeroQuestionMarkCircleIconOutline',
  color: 'yellow',
  to: 'MissedCallsList',
  hasConfirmed: false
})

const currentForm = ref<Form>({} as Form)

const title = computed(() => t('pages.missedCalls.missedCallsDetailPage.title'))

const validForm = computed(() => {
  const validFormData = form.message.length > 1 && form.messageLink.length > 1
  const validContent = !invalidCharacters.value && validPhoneNumber.value && !invalidSmsContent.value

  if (form.missedCallsMethod.value) {
    return validFormData && validContent && form.storePhoneNumber.length > 1
  } else {
    return validFormData && validContent
  }
})

const hasBeenEdited = computed(() => !areEquals(form, currentForm.value))

const getMerchantInfo = () => {
  return axios
    .get(`/merchants/${user.value.merchant.id}`)
    .then((response: any) => (merchant.value = response.data.data))
    .catch(() => {
      notification.create('error', {
        text: t('pages.missedCalls.missedCallsAnalyticsPage.error'),
        timeout: 2000
      })
    })
}

const getMissedCallsInfo = () => {
  return axios
    .get(`/missed-calls/stores/${storeId}`)
    .then((response: any) => {
      if (response.data.data) {
        const data = response.data.data
        missedCallStoreId.value = data.id

        if (data.smsContent) {
          form.message = data.smsContent
        }
        form.messageLink = data.shortLink?.link || ''
        form.shortedMessageLink = data.shortLink?.shortLink || ''
        form.vocalMessage = data.voiceMessage
        form.audioName = data?.audioName || ''

        form.audioName = data?.audioName || dayjs().format('DD/MM/YYYY').toString().replaceAll('/', '-')
        getSelectedMethod(data.isCallRedirectedToShop)

        if (data.landingPageTitle) {
          form.landingPageTitle = data.landingPageTitle
        }
        if (data.landingPageDescription) {
          form.landingPageDescription = data.landingPageDescription
        }
        form.storePhoneNumber = data.shopPhoneNumber
        form.isLandingPageEnabled = data.isLandingPageEnabled
        const result = detectCountryFromNumber(data.shopPhoneNumber)
        if (result) {
          countryCode.value = result.countryCode
        }
      }
      cloneForm()
    })
    .catch(() => {
      return
    })
}

const cloneForm = () => {
  currentForm.value = useCloned(form)
}

const getSelectedMethod = (methodData: boolean) => {
  form.missedCallsMethod = methods.value.find((method) => method.value === methodData)
}

const handleScroll = () => {
  if (rightColumn.value) {
    const rightColumnTop = rightColumn.value.getBoundingClientRect().top
    isFixed.value = rightColumnTop < 20
  }
}

const checkAllowedCharacters = (e: Event) => {
  const target = e.target as HTMLInputElement
  invalidCharacters.value = /[^\p{L}\p{N}\p{P}\p{Z}\p{M}^$€<>=+¨~−\n]/gu.test(target.value)
}

const checkPhoneNumber = (phoneObject: PhoneObject) => {
  validPhoneNumber.value = phoneObject.isValid
  form.storePhoneNumber = phoneObject.internalPhoneNumber
}

const uploadFile = (file: File, name: string) => {
  vocalMessageUpdated.value = true
  form.audioName = name
  form.vocalMessage = file
}

const renameFile = (filename: string) => {
  form.audioName = filename
}

const resetVocalMessage = () => {
  vocalMessageUpdated.value = true
  form.vocalMessage = null
  form.audioName = t('pages.missedCalls.missedCallsDetailPage.audioRecorder.defaultName')
}

const goToPreviousPage = () => {
  cancelModal.hasConfirmed = true
  router.push({ name: cancelModal.to })
}

const deleteMissedCallStore = async () => {
  if (!missedCallStoreId.value) return

  isDeleting.value = true
  try {
    await axios.delete(`missed-calls/stores/${storeId}`)
    notification.create('success', {
      text: t('pages.missedCalls.missedCallsDetailPage.delete.success')
    })
    isDeleteModalOpen.value = false
    cancelModal.hasConfirmed = true
    router.push({ name: 'MissedCallsList' })
  } catch {
    notification.create('error', {
      text: t('pages.missedCalls.missedCallsDetailPage.delete.error')
    })
  } finally {
    isDeleting.value = false
  }
}

const save = () => {
  if (!validForm.value) {
    notification.create('error', {
      text: t('pages.missedCalls.missedCallsDetailPage.errors.missingFields'),
      timeout: 2000
    })
    return
  }
  if (!isUrlValid(form.messageLink)) {
    notification.create('error', {
      text: t('pages.missedCalls.missedCallsDetailPage.errors.invalidUrl'),
      timeout: 2000
    })
    return
  }
  if (isProcessing.value) {
    return
  }
  isProcessing.value = true

  let params: any = {
    storeId: storeId,
    smsContent: form.message,
    smsLink: form.messageLink,
    isCallRedirectedToShop: form.missedCallsMethod.value,
    audioName: form.audioName,
    shopPhoneNumber: form.storePhoneNumber,
    isLandingPageEnabled: form.isLandingPageEnabled,
    landingPageTitle: form.landingPageTitle,
    landingPageDescription: form.landingPageDescription
  }

  if (vocalMessageUpdated.value) {
    params = {
      ...params,
      voiceMessage: form.vocalMessage
    }
  }

  cancelModal.hasConfirmed = true

  axios({
    method: missedCallStoreId.value ? 'patch' : 'post',
    url: `/missed-calls/stores/${storeId}`,
    data: params
  })
    .then(() => {
      redirectToNextPage()
    })
    .catch(() => {
      notification.create('error', {
        text: t('notifications.save.error'),
        timeout: 2000
      })
    })
    .finally(() => (isProcessing.value = false))
}

const redirectToNextPage = () => {
  notifyFormUpdated()
  if (!missedCallStoreId.value) {
    router.push({ name: 'MissedCallsList' })
  }
}

const notifyFormUpdated = () => {
  notification.create('success', {
    text: t('pages.missedCalls.missedCallsDetailPage.notification.success'),
    persist: true
  })
}

const openHelpCenterLink = () => {
  const helpCenterLink = t('pages.missedCalls.missedCallsDetailPage.message.form.behavior.methods.helpCenterLink')
  window.open(helpCenterLink, '_blank')
}

onBeforeRouteLeave((to, from, next) => {
  cancelModal.to = to.name as string
  if (hasBeenEdited.value && !cancelModal.hasConfirmed) {
    cancelModal.isDisplayed = true
  } else {
    next()
  }
})

onMounted(async () => {
  store.value = stores.value.filter((s: Store) => s.id == parseInt(storeId))[0]
  await Promise.all([getMerchantInfo(), getMissedCallsInfo()])

  isLoading.value = false
})

onUnmounted(() => {
  if (document.querySelector('main')) {
    document.querySelector('main')?.removeEventListener('scroll', handleScroll)
  }
})
</script>

<template>
  <TheLoadingPage v-if="isLoading" />
  <div v-else>
    <div>
      <TheDetailHeader
        :title="title"
        :parent="{ label: t('pages.missedCalls.missedCallsDetailPage.title'), to: { name: 'MissedCallsList' } }"
      >
        <template #actions>
          <div class="flex items-center gap-2">
            <Button v-if="canShowDeleteButton" variant="outline-destructive" @click="isDeleteModalOpen = true">
              <Trash2 />
              {{ t('global.words.delete') }}
            </Button>
            <Button variant="default-primary" :disabled="!validForm || isProcessing" @click="save">
              <Spinner v-if="isProcessing" size="sm" class="text-current" />
              {{ t(missedCallStoreId ? 'global.words.save' : 'pages.missedCalls.missedCallsDetailPage.create') }}
            </Button>
          </div>
        </template>
      </TheDetailHeader>
      <div class="space-y-8 divide-y divide-border px-6 pb-6 sm:px-12 sm:pb-12">
        <div class="lg:grid lg:grid-cols-3 lg:gap-4">
          <div class="md:col-span-2">
            <div class="border bg-white sm:rounded-md">
              <div class="border bg-white sm:rounded-md">
                <div class="border-b px-8 py-8">
                  <h3 class="text-lg font-medium leading-6 text-foreground">
                    {{ t('pages.missedCalls.missedCallsDetailPage.message.form.behavior.title') }}
                  </h3>
                </div>
                <div class="grid grid-cols-1 gap-x-4 gap-y-6 p-8 sm:grid-cols-6">
                  <div class="sm:col-span-6">
                    <AppRadioCard v-model="form.missedCallsMethod" class="grid grid-cols-2 gap-4" :options="methods" />
                    <BaseTip v-if="form.missedCallsMethod.type === 'direct'" class="mt-4" severity="info">
                      <template #text>
                        <p class="mb-2">
                          {{
                            t(`pages.missedCalls.missedCallsDetailPage.message.form.behavior.methods.direct.tipTitle`)
                          }}
                        </p>
                        <Button variant="default-primary" @click="openHelpCenterLink">
                          {{ t('pages.missedCalls.missedCallsDetailPage.message.form.behavior.methods.knowMore') }}
                          <BaseIcon name="HeroArrowTopRightOnSquareIconOutline" class="h-4 w-4" />
                        </Button>
                      </template>
                    </BaseTip>
                    <div v-if="form.missedCallsMethod.value" class="mt-5 sm:col-span-6">
                      <BaseVueTelInput
                        v-model="form.storePhoneNumber"
                        :label="t('pages.missedCalls.missedCallsDetailPage.message.form.phoneNumber')"
                        required
                        :country-code="countryCode"
                        @update:phone-object="checkPhoneNumber"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-6 border bg-white sm:rounded-md">
              <div class="border-b px-8 py-8">
                <h3 class="text-lg font-medium leading-6 text-foreground">
                  {{ t('pages.missedCalls.missedCallsDetailPage.message.title') }}
                </h3>
                <p class="mt-1 text-sm text-muted-foreground">
                  {{ t('pages.missedCalls.missedCallsDetailPage.message.description') }}
                </p>
              </div>
              <!-- Missed calls content -->
              <div class="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 px-8 pb-8 sm:grid-cols-6">
                <MissedCallsAudio
                  v-model="form.vocalMessage"
                  :original-audio-name="form.audioName"
                  @update="uploadFile"
                  @rename-file="renameFile"
                  @reset="resetVocalMessage"
                />
              </div>
            </div>
            <!-- Missed calls mode -->
            <div class="mt-6 border bg-white sm:rounded-md">
              <div class="border-b px-8 py-8">
                <h3 class="text-lg font-medium leading-6 text-foreground">
                  {{ t('pages.missedCalls.missedCallsDetailPage.message.form.smsSent.title') }}
                </h3>
                <p class="mt-1 text-sm text-muted-foreground">
                  {{ t('pages.missedCalls.missedCallsDetailPage.message.form.smsSent.description') }}
                </p>
              </div>
              <div class="mt-6 grid grid-cols-1 gap-x-4 gap-y-6 px-8 pb-8 sm:grid-cols-6">
                <TextareaWithVars
                  v-model="form.message"
                  :max-length="145"
                  :i18n-strings="{
                    textareaLabel: t('pages.missedCalls.missedCallsDetailPage.message.form.smsSent.smsText'),
                    characters: t('global.words.characters'),
                    insertVar: t('global.customVars.insertLabel'),
                    errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji')
                  }"
                  :custom-vars="[]"
                  :sanitize-for-gsm="true"
                  @has-error="invalidSmsContent = $event"
                />
                <div class="sm:col-span-6">
                  <label for="messageLink" class="block text-sm text-foreground">
                    {{ t('pages.missedCalls.missedCallsDetailPage.message.form.smsSent.smsLink') }}
                    <sup class="ml-1 text-red-600">*</sup>
                  </label>
                  <p class="text-sm text-muted-foreground">
                    {{ t('pages.missedCalls.missedCallsDetailPage.message.form.smsSent.linkDescription') }}
                  </p>
                  <div class="mt-1">
                    <AppFormInput
                      id="messageLink"
                      v-model="form.messageLink"
                      name="messageLink"
                      placeholder="https://example.com"
                      type="url"
                      pattern="https://.*"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-6 border bg-white sm:rounded-md">
              <div class="border-b px-8 py-8">
                <h3 class="text-lg font-medium leading-6 text-foreground">
                  {{ t('pages.missedCalls.missedCallsDetailPage.message.form.userData.title') }}
                </h3>
                <p class="mt-1 text-sm text-muted-foreground">
                  {{ t('pages.missedCalls.missedCallsDetailPage.message.form.userData.description') }}
                </p>
              </div>
              <div class="p-8">
                <div class="mt-1">
                  <AppCheckboxField
                    id="hasConsent"
                    v-model="form.isLandingPageEnabled"
                    :label="t('pages.missedCalls.missedCallsDetailPage.message.form.userData.hasOptin')"
                    :description="
                      t('pages.missedCalls.missedCallsDetailPage.message.form.userData.hasOptinDescription')
                    "
                  />
                </div>
                <template v-if="form.isLandingPageEnabled">
                  <div class="mt-5">
                    <label for="landingPageTitle" class="block text-sm text-foreground">
                      {{ t('pages.missedCalls.missedCallsDetailPage.message.form.userData.landingPageTitle') }}
                    </label>
                  </div>
                  <div class="mt-1">
                    <AppFormInput
                      id="landingPageTitle"
                      v-model="form.landingPageTitle"
                      name="landingPageTitle"
                      :placeholder="
                        t('pages.missedCalls.missedCallsDetailPage.message.form.userData.defaultTitleLandingPage')
                      "
                    />
                  </div>
                  <div class="mt-5">
                    <label for="landingPageDescription" class="block text-sm text-foreground">
                      {{ t('pages.missedCalls.missedCallsDetailPage.message.form.userData.landingPageDescription') }}
                    </label>
                    <div class="mt-1">
                      <AppFormInput
                        id="landingPageDescription"
                        v-model="form.landingPageDescription"
                        name="landingPageDescription"
                        :placeholder="
                          t(
                            'pages.missedCalls.missedCallsDetailPage.message.form.userData.defaultDescriptionLandingPage'
                          )
                        "
                        @input="checkAllowedCharacters"
                      />
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div ref="rightColumn">
            <div
              class="mt-8 lg:mt-0"
              :class="[
                isFixed ? 'fixed right-12 top-4 w-3/12 translate-y-2' : 'w-full lg:ml-3',
                'right-0 transform transition-transform duration-700'
              ]"
            >
              <div class="h-96 overflow-hidden bg-white shadow-md sm:rounded-lg">
                <div class="border-b px-4 py-5 sm:px-6">
                  <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                    <div class="ml-2 mt-2">
                      <h3 class="text-lg font-medium leading-6 text-foreground">
                        {{ t('pages.missedCalls.missedCallsDetailPage.preview') }}
                      </h3>
                    </div>
                  </div>
                </div>
                <div class="relative mx-auto flex h-72 justify-center overflow-hidden px-4 pt-6">
                  <BasePhone size="md" hide-bottom>
                    <div class="h-full bg-white">
                      <div class="border-b-2 bg-muted py-4 text-center text-xl font-medium text-foreground">
                        {{ merchantName }}
                      </div>
                      <div class="mx-4 mt-4 flex flex-col gap-4">
                        <div v-if="form.message" class="break-words rounded-xl bg-indigo-500 p-4 text-xl text-white">
                          {{ form.message }} {{ form.shortedMessageLink }}
                        </div>
                      </div>
                    </div>
                  </BasePhone>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <BaseModal
        :is-open="cancelModal.isDisplayed"
        :link-text="cancelModal.linkText"
        :text-title="cancelModal.title"
        :text="cancelModal.text"
        :cancel="cancelModal.cancel"
        :icon="cancelModal.icon"
        :icon-color="cancelModal.color"
        @click="goToPreviousPage"
        @close="cancelModal.isDisplayed = false"
      />
      <AlertDialog v-model:open="isDeleteModalOpen">
        <AlertDialogContent class="sm:max-w-sm">
          <AlertDialogHeader>
            <BaseSupportConnection>
              <AlertDialogTitle>{{ t('pages.missedCalls.missedCallsDetailPage.delete.title') }}</AlertDialogTitle>
            </BaseSupportConnection>
            <AlertDialogDescription>
              {{ t('pages.missedCalls.missedCallsDetailPage.delete.message', { name: store.name }) }}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel :disabled="isDeleting">{{ t('global.words.cancel') }}</AlertDialogCancel>
            <Button variant="destructive" :disabled="isDeleting" @click="deleteMissedCallStore">
              <Spinner v-if="isDeleting" size="sm" class="text-current" />
              <Trash2 v-else />
              {{ t('global.words.delete') }}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </div>
</template>
